import { BusinessOpeningHour } from '@/types/index';

/**
 * Simple utility functions for opening hours - replacing the complex useOpeningHours hook
 * These functions work with opening hours data from business.opening_hours
 */

export interface OpeningHoursValidation {
  severity: 'error' | 'warning' | 'info';
  message: string;
  suggestedAction?: string;
}

/**
 * Format opening hours for display
 * @param openingHours - Array of opening hours from business data
 * @param dayName - Day name like "Monday", "Tuesday", etc.
 * @returns Formatted hours string like "09:00 - 17:00" or "Closed all day"
 */
export function formatOpeningHours(
  openingHours: BusinessOpeningHour[] | null | undefined,
  dayName: string
): string {
  if (!openingHours || openingHours.length === 0) {
    return "Hours not available";
  }

  const todayHours = openingHours.find(hour => hour.day_of_week === dayName);

  if (!todayHours) {
    return "Closed all day";
  }

  if (todayHours.is_closed_all_day) {
    return "Closed all day";
  }

  if (todayHours.is_24_hours) {
    return "Open 24 hours";
  }

  if (todayHours.opening_time && todayHours.closing_time) {
    return `${todayHours.opening_time.substring(0, 5)} - ${todayHours.closing_time.substring(0, 5)}`;
  }

  return "Hours not available";
}

/**
 * Check if business is currently open
 * @param openingHours - Array of opening hours from business data
 * @param date - Date to check (defaults to now)
 * @returns True if business is open
 */
export function isBusinessOpen(
  openingHours: BusinessOpeningHour[] | null | undefined,
  date: Date = new Date()
): boolean {
  if (!openingHours || openingHours.length === 0) {
    return false;
  }

  const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  const currentDayName = dayNames[date.getDay()];
  const todayHours = openingHours.find(hour => hour.day_of_week === currentDayName);

  if (!todayHours || todayHours.is_closed_all_day) {
    return false;
  }

  if (todayHours.is_24_hours) {
    return true;
  }

  if (!todayHours.opening_time || !todayHours.closing_time) {
    return false;
  }

  const currentTime = `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  return currentTime >= todayHours.opening_time && currentTime <= todayHours.closing_time;
}

/**
 * Validate if a shift time is within business hours
 * @param shiftTime - Object with startTime, endTime strings and date
 * @param openingHours - Array of opening hours from business data
 * @returns Validation result
 */
export function validateShiftTime(
  shiftTime: {
    startTime: string; // "HH:MM" format
    endTime: string;   // "HH:MM" format
    date: Date;
  },
  openingHours: BusinessOpeningHour[] | null | undefined
): OpeningHoursValidation {
  if (!openingHours || openingHours.length === 0) {
    return {
      severity: 'info',
      message: 'No opening hours configured - shift can be scheduled at any time'
    };
  }

  const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  const dayName = dayNames[shiftTime.date.getDay()];
  const dayHours = openingHours.find(hour => hour.day_of_week === dayName);

  if (!dayHours) {
    return {
      severity: 'warning',
      message: `No opening hours set for ${dayName}`,
      suggestedAction: 'Configure opening hours for this day'
    };
  }

  if (dayHours.is_closed_all_day) {
    return {
      severity: 'warning',
      message: `Business is closed all day on ${dayName}`,
      suggestedAction: 'Consider scheduling on a different day'
    };
  }

  if (dayHours.is_24_hours) {
    return {
      severity: 'info',
      message: 'Business is open 24 hours - shift can be scheduled at any time'
    };
  }

  if (!dayHours.opening_time || !dayHours.closing_time) {
    return {
      severity: 'warning',
      message: `Opening hours not properly configured for ${dayName}`,
      suggestedAction: 'Configure opening and closing times'
    };
  }

  // Check if shift is within opening hours
  const businessStart = dayHours.opening_time;
  const businessEnd = dayHours.closing_time;

  if (shiftTime.startTime < businessStart || shiftTime.endTime > businessEnd) {
    return {
      severity: 'warning',
      message: `Shift extends outside business hours (${businessStart} - ${businessEnd})`,
      suggestedAction: 'Adjust shift times to fit within business hours'
    };
  }

  return {
    severity: 'info',
    message: 'Shift is within business hours'
  };
}

/**
 * Get opening hours for a specific day
 * @param openingHours - Array of opening hours from business data
 * @param dayName - Day name like "Monday", "Tuesday", etc.
 * @returns Opening hours for the day or null
 */
export function getOpeningHoursForDay(
  openingHours: BusinessOpeningHour[] | null | undefined,
  dayName: string
): BusinessOpeningHour | null {
  if (!openingHours || openingHours.length === 0) {
    return null;
  }

  return openingHours.find(hour => hour.day_of_week === dayName) || null;
}

/**
 * Check if a specific day is a business day (not closed all day)
 * @param openingHours - Array of opening hours from business data
 * @param dayName - Day name like "Monday", "Tuesday", etc.
 * @returns True if business is open on this day
 */
export function isBusinessDay(
  openingHours: BusinessOpeningHour[] | null | undefined,
  dayName: string
): boolean {
  const dayHours = getOpeningHoursForDay(openingHours, dayName);
  return dayHours ? !dayHours.is_closed_all_day : false;
}